import React, { useCallback, useContext, useState } from 'react';
import { Alert, AlertColor, Snackbar } from '@mui/material';
import { CombinedError } from 'urql';

type FlashMessageContextValue = {
  addFlashMessage(severity: AlertColor, message: string): void;
  clearFlashMessage(): void;
  // Opt-in helper for the common "useMutation, then surface response.error as a toast" pattern --
  // NOT wired in automatically for every mutation. Plenty of call sites fire mutations as background
  // side effects the user never consciously waits on (chat read-receipts, reaction toggles,
  // notification acknowledgements) where a failure toast would just be noise; those should keep
  // ignoring `response.error` outright. Call sites that want inline/field-level error display instead
  // of a toast should also skip this and handle `response.error` themselves.
  // Returns whether there was an error, so callers can `if (flashMutationError(response.error)) return;`.
  flashMutationError(error: CombinedError | null | undefined, fallbackMessage?: string): boolean;
};

const FlashMessageContext = React.createContext<FlashMessageContextValue>({
  addFlashMessage: () => null,
  clearFlashMessage: () => null,
  flashMutationError: () => false,
});

type FlashMessageProviderProps = {
  children: React.ReactNode;
};

function FlashMessageProvider({ children }: FlashMessageProviderProps) {
  const [state, setState] = useState<{ severity: AlertColor; message: string }>(null);
  const addFlashMessage = useCallback(
    (severity: AlertColor, message: string) => setState({ severity, message }),
    [setState],
  );
  const clearFlashMessage = useCallback(() => setState(null), [setState]);
  const flashMutationError = useCallback(
    (error: CombinedError | null | undefined, fallbackMessage = 'Something went wrong') => {
      if (!error) {
        return false;
      }
      addFlashMessage('error', error.graphQLErrors[0]?.message ?? error.message ?? fallbackMessage);
      return true;
    },
    [addFlashMessage],
  );

  return (
    <FlashMessageContext.Provider value={{ addFlashMessage, clearFlashMessage, flashMutationError }}>
      <>
        {state && (
          <Snackbar
            open
            autoHideDuration={6000}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={(_, reason) => {
              if (reason !== 'clickaway') {
                setState(null);
              }
            }}
          >
            <Alert severity={state.severity} variant="filled" onClose={() => setState(null)}>
              {state.message}
            </Alert>
          </Snackbar>
        )}
        {children}
      </>
    </FlashMessageContext.Provider>
  );
}

export function useFlashMessageContext() {
  return useContext(FlashMessageContext);
}

export default FlashMessageProvider;
